import React, { FormEvent, useEffect, useId, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useRequiredFields } from 'reusable/useRequiredFields';
import { OPERATOR_COMPANIES_PATH, operatorCompanyPath } from 'router/pathName';
import CompanyFields, { emptyCompany, trimmedCompany, useTimeZones } from './CompanyFields';
import { CompanyField, CompanyValues, createCompany } from './operatorApi';
import { ErrorNotice, inputClass, isUnresolved, primaryButton, secondaryButton, useOutcomeMessages } from './OperatorUi';

/**
 * YAC-2026-009 PR5b。会社の登録（運営）。最初の管理者のメールアドレスと名前を一緒に入れると、会社の登録と
 * 同時に、初期パスワード付きの招待のメールを送る（サーバーは1つのトランザクションで作り、メールを送れなければ
 * 会社も取り消して 502 email_not_sent）。X-Request-ID は submitIdempotentCreate が付ける。
 */
const CompanyCreate: React.FC = () => {
  const { t } = useTranslation('layout');
  const navigate = useNavigate();
  const outcomeMessages = useOutcomeMessages();
  const nameId = useId();
  const managerEmailId = useId();
  const managerNameId = useId();
  const inviteId = useId();
  const managerHintId = useId();
  const [values, setValues] = useState<CompanyValues>(emptyCompany);
  const [inviteManager, setInviteManager] = useState(true);
  const [manager, setManager] = useState({ email: '', name: '' });
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  // #387 の Codex の指摘 4368590863。結果が分からないまま。入力を送った値で固定し、同じ内容で送り直すだけにする。
  const [unresolved, setUnresolved] = useState(false);
  const locked = busy || unresolved;
  const { zones, failed: zonesFailed } = useTimeZones();
  const companyRequired = useRequiredFields({ name: nameId });
  const managerRequired = useRequiredFields({ email: managerEmailId, name: managerNameId });
  // 画面を離れた後に届いた結果では、移動や表示をしない。React.StrictMode の開発の描画では effect が
  // setup → cleanup → setup と2回動くので、setup で true に戻す（#387 の Codex の指摘 4368518468。
  // AddClient・AddNewTaskDialog と同じ形）。
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const setField = (key: CompanyField, value: string) => setValues((current) => ({ ...current, [key]: value }));

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (busy) return;
    const company = trimmedCompany(values);
    const invitation = inviteManager ? { email: manager.email.trim(), name: manager.name.trim() } : null;
    // 会社名 → 管理者の欄の順に、最初の空欄へフォーカスを移す。
    const companyOk = companyRequired.check({ name: company.name });
    const managerOk = invitation === null || !companyOk ? true : managerRequired.check(invitation);
    if (!companyOk || !managerOk) return;
    // 送る値を欄にも出す（前後の空白を除いた値。固定したときに、送った内容と同じに見えるように）。
    setValues(company);
    if (invitation) setManager(invitation);
    setBusy(true);
    setErrors([]);
    const outcome = await createCompany(company, invitation);
    if (!mounted.current) return;
    setBusy(false);
    const failed = outcomeMessages(outcome);
    setUnresolved(isUnresolved(outcome));
    if (failed) {
      setErrors(failed);
      return;
    }
    const created = outcome.status === 'created' ? outcome.response?.data : null;
    const notice = created?.manager?.email
      ? t('operator.company.created-invited', { email: created.manager.email })
      : t('operator.company.created');
    navigate(created?.id ? operatorCompanyPath(created.id) : OPERATOR_COMPANIES_PATH, { state: { notice } });
  };

  return (
    <section className="flex flex-col gap-4 px-4 py-3">
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-title font-bold text-neutral/300">{t('operator.company.new-title')}</h1>
        <Link to={OPERATOR_COMPANIES_PATH} className="text-primary/300 underline">
          {t('operator.company.back')}
        </Link>
      </div>
      <form onSubmit={submit} noValidate className="flex flex-col gap-6 rounded-md bg-white p-6">
        <fieldset className="flex flex-col gap-4">
          <legend className="mb-2 text-subtitle-1 font-bold text-neutral/300">{t('operator.company.info')}</legend>
          <CompanyFields
            values={values}
            onChange={setField}
            nameId={nameId}
            requiredFields={companyRequired}
            zones={zones}
            zonesFailed={zonesFailed}
            disabled={locked}
          />
        </fieldset>
        <fieldset className="flex flex-col gap-4">
          <legend className="mb-2 text-subtitle-1 font-bold text-neutral/300">{t('operator.company.first-manager')}</legend>
          <div className="flex items-center gap-2">
            <input
              id={inviteId}
              type="checkbox"
              className="h-4 w-4"
              checked={inviteManager}
              disabled={locked}
              aria-describedby={managerHintId}
              onChange={(event) => setInviteManager(event.target.checked)}
            />
            <label htmlFor={inviteId} className="text-body-2 text-neutral/300">
              {t('operator.company.invite-first-manager')}
            </label>
          </div>
          <p id={managerHintId} className="text-caption text-neutral/100">
            {t('operator.company.first-manager-hint')}
          </p>
          {inviteManager ? (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="flex flex-col gap-1">
                <label htmlFor={managerEmailId} className="text-subtitle-2 text-neutral/300">
                  {t('operator.company.manager-email')}
                  <span className="ml-1 text-caption text-red-700" aria-hidden="true">
                    {t('operator.required-mark')}
                  </span>
                </label>
                <input
                  id={managerEmailId}
                  type="email"
                  autoComplete="off"
                  className={inputClass}
                  value={manager.email}
                  disabled={locked}
                  onChange={(event) => {
                    setManager((current) => ({ ...current, email: event.target.value }));
                    managerRequired.clear('email');
                  }}
                  {...managerRequired.fieldProps('email')}
                />
                {managerRequired.message('email')}
              </div>
              <div className="flex flex-col gap-1">
                <label htmlFor={managerNameId} className="text-subtitle-2 text-neutral/300">
                  {t('operator.company.manager-name')}
                  <span className="ml-1 text-caption text-red-700" aria-hidden="true">
                    {t('operator.required-mark')}
                  </span>
                </label>
                <input
                  id={managerNameId}
                  type="text"
                  autoComplete="off"
                  className={inputClass}
                  value={manager.name}
                  disabled={locked}
                  onChange={(event) => {
                    setManager((current) => ({ ...current, name: event.target.value }));
                    managerRequired.clear('name');
                  }}
                  {...managerRequired.fieldProps('name')}
                />
                {managerRequired.message('name')}
              </div>
            </div>
          ) : null}
        </fieldset>
        <ErrorNotice messages={errors} />
        {unresolved ? <p className="text-body-2 text-neutral/300">{t('operator.unresolved-note')}</p> : null}
        <div className="flex justify-end gap-4">
          <Link to={OPERATOR_COMPANIES_PATH} className={secondaryButton}>
            {t('ui.cancel', { ns: 'common' })}
          </Link>
          <button type="submit" className={primaryButton} disabled={busy}>
            {busy ? t('operator.sending') : unresolved ? t('operator.resend-same') : t('operator.company.submit-create')}
          </button>
        </div>
      </form>
    </section>
  );
};

export default CompanyCreate;
